/**
 * ViewAsContext v1.1 — Superadmin tier preview
 *
 * Lets an admin preview the app as any plan tier.
 *
 * v1.1 (T779, 2026-10-14): the floating bottom-left "View As..." pill is gone.
 * The tier picker now sits in the top header next to the "Admin view" switch and
 * only appears while admin view is on (ViewAsTierSelect, rendered by TopBar).
 * Turning admin view off clears any preview, so the user view is always the
 * real one.
 *
 * File: src/components/layout/ViewAsContext.tsx
 */

import { createContext, useContext, useState, useEffect } from "react";
import { useAdminView } from "@/components/layout/AdminViewContext";

interface ViewAsState {
  /** The tier the UI should render as. null = use real profile tier */
  overrideTier: string | null;
  setOverrideTier: (tier: string | null) => void;
  /** Convenience: returns overrideTier if set, otherwise the real tier */
  effectiveTier: (realTier: string) => string;
}

const ViewAsContext = createContext<ViewAsState>({
  overrideTier: null,
  setOverrideTier: () => {},
  effectiveTier: (r) => r,
});

export function useViewAs() {
  return useContext(ViewAsContext);
}

export const TIER_OPTIONS = [
  { value: "free", label: "Free (expired trial)" },
  { value: "pro", label: "Pro" },
  { value: "agency", label: "Agency Manager" },
  { value: "enterprise", label: "Enterprise Manager" },
];

export function ViewAsProvider({ children }: { children: React.ReactNode }) {
  const { showAdmin } = useAdminView();
  const [overrideTier, setOverrideTier] = useState<string | null>(null);

  // A preview only exists inside admin view.
  useEffect(() => {
    if (!showAdmin) setOverrideTier(null);
  }, [showAdmin]);

  const effectiveTier = (realTier: string) => overrideTier || realTier;

  return (
    <ViewAsContext.Provider value={{ overrideTier, setOverrideTier, effectiveTier }}>
      {children}
    </ViewAsContext.Provider>
  );
}

/** Compact plan-preview picker for the top header. Renders only in admin view. */
export function ViewAsTierSelect() {
  const { showAdmin } = useAdminView();
  const { overrideTier, setOverrideTier } = useViewAs();
  if (!showAdmin) return null;
  return (
    <select
      value={overrideTier ?? ""}
      onChange={(e) => setOverrideTier(e.target.value || null)}
      title="Preview the app as a different plan"
      aria-label="Preview the app as a different plan"
      className={`hidden md:block h-7 rounded-md border px-1.5 text-[11px] bg-background ${
        overrideTier ? "border-amber-500/60 text-amber-500" : "border-border text-muted-foreground"
      }`}
    >
      <option value="">Real plan</option>
      {TIER_OPTIONS.map((t) => (
        <option key={t.value} value={t.value}>Preview: {t.label}</option>
      ))}
    </select>
  );
}
